import React, { useState } from 'react';
import { useToast, ToastContainer } from '../common/Toast';
import userService from '../../services/users';

const SimpleAssignTest: React.FC = () => {
  const { toasts, showSuccess, showError } = useToast();
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<any[]>([]);

  const testAssignments = async () => {
    setLoading(true);
    setResults([]);
    
    const testCases = [
      {
        name: 'Test 1: Assignation permanente simple',
        userId: '5',
        siteId: 1,
        roleId: 'admin',
        sessionType: 'PERMANENTE' as const,
        isDefault: true,
        startDate: undefined,
        endDate: undefined
      },
      {
        name: 'Test 2: Assignation temporaire',
        userId: '5',
        siteId: 2,
        roleId: 'operateur',
        sessionType: 'TEMPORAIRE' as const,
        isDefault: false,
        startDate: '2025-01-20',
        endDate: '2025-02-20'
      }
    ];

    const newResults: any[] = [];

    for (const testCase of testCases) {
      try {
        console.log(`🧪 Exécution: ${testCase.name}`);
        
        const result = await userService.assignSiteToUser(
          testCase.userId,
          testCase.siteId,
          testCase.roleId,
          testCase.sessionType,
          testCase.isDefault || false,
          testCase.startDate,
          testCase.endDate
        );
        
        console.log(`✅ Succès: ${testCase.name}`, result);
        
        newResults.push({
          name: testCase.name,
          status: 'success',
          result,
          timestamp: new Date().toISOString()
        });
        
        showSuccess('Test réussi', `${testCase.name} - ${result.message}`);
        
      } catch (error: any) {
        console.error(`❌ Échec: ${testCase.name}`, error);
        
        newResults.push({
          name: testCase.name,
          status: 'error',
          error: error.message,
          timestamp: new Date().toISOString()
        });
        
        showError('Test échoué', `${testCase.name} - ${error.message}`);
      }
      
      // Petite pause entre les tests
      await new Promise(resolve => setTimeout(resolve, 1000));
    }
    
    setResults(newResults);
    setLoading(false);
  };

  const clearResults = () => {
    setResults([]);
  };

  return (
    <div className="min-h-screen bg-gray-50 p-8">
      <div className="max-w-4xl mx-auto">
        <div className="bg-white rounded-xl shadow-sm p-6">
          <h1 className="text-2xl font-bold text-gray-900 mb-6">🧪 Test Simple d'Assignation</h1>
          
          <div className="space-y-6">
            {/* Informations */}
            <div className="bg-blue-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-blue-900 mb-2">Informations de test</h3>
              <div className="text-sm text-blue-700 space-y-1">
                <p>• <strong>Utilisateur de test:</strong> ID 5</p>
                <p>• <strong>Sites de test:</strong> ID 1 et 2</p>
                <p>• <strong>Rôles de test:</strong> admin et operateur</p>
                <p>• <strong>Types de session:</strong> PERMANENTE et TEMPORAIRE</p>
              </div>
            </div>

            {/* Boutons d'action */}
            <div className="flex gap-4">
              <button
                onClick={testAssignments}
                disabled={loading}
                className="inline-flex items-center gap-2 rounded-xl px-6 py-3 text-lg font-medium bg-green-600 text-white hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                {loading ? (
                  <>
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                    Tests en cours...
                  </>
                ) : (
                  <>
                    🚀 Lancer les Tests d'Assignation
                  </>
                )}
              </button>
              
              <button
                onClick={clearResults}
                disabled={loading || results.length === 0}
                className="inline-flex items-center gap-2 rounded-xl px-4 py-3 text-sm font-medium bg-gray-600 text-white hover:bg-gray-700 disabled:bg-gray-400 disabled:cursor-not-allowed transition-colors"
              >
                🗑️ Effacer les résultats
              </button>
            </div>

            {/* Résultats des tests */}
            {results.length > 0 && (
              <div className="space-y-4">
                <h3 className="text-lg font-medium text-gray-900">📊 Résultats des Tests</h3>
                
                {results.map((result, index) => (
                  <div key={index} className={`border rounded-lg p-4 ${
                    result.status === 'success' 
                      ? 'border-green-200 bg-green-50' 
                      : 'border-red-200 bg-red-50'
                  }`}>
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="font-medium text-gray-900">{result.name}</h4>
                      <span className={`text-sm font-medium ${
                        result.status === 'success' ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {result.status === 'success' ? '✅ Succès' : '❌ Échec'}
                      </span>
                    </div>
                    
                    <div className="text-sm text-gray-600 mb-2">
                      <span className="text-gray-500">Exécuté le:</span> {new Date(result.timestamp).toLocaleString('fr-FR')}
                    </div>
                    
                    {result.status === 'success' && result.result && (
                      <div className="bg-white p-3 rounded border">
                        <div className="text-sm">
                          <p><strong>Message:</strong> {result.result.message}</p>
                          <p><strong>ID d'assignation:</strong> {result.result.assignment_id}</p>
                          {result.result.session_id && (
                            <p><strong>ID de session:</strong> {result.result.session_id}</p>
                          )}
                        </div>
                      </div>
                    )}
                    
                    {result.status === 'error' && result.error && (
                      <div className="bg-white p-3 rounded border">
                        <div className="text-sm text-red-600">
                          <strong>Erreur:</strong> {result.error}
                        </div>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* Instructions */}
            <div className="bg-yellow-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-yellow-900 mb-2">📋 Instructions</h3>
              <div className="text-sm text-yellow-800 space-y-2">
                <p>1. Cliquez sur "Lancer les Tests d'Assignation"</p>
                <p>2. Attendez que tous les tests soient terminés</p>
                <p>3. Vérifiez les résultats ci-dessus</p>
                <p>4. Consultez la console du navigateur pour les détails techniques</p>
                <p>5. Si des tests échouent, utilisez le composant ApiDiagnostic pour diagnostiquer</p>
              </div>
            </div>

            {/* Liens utiles */}
            <div className="bg-gray-50 rounded-lg p-4">
              <h3 className="text-lg font-medium text-gray-900 mb-2">🔗 Liens utiles</h3>
              <div className="text-sm text-gray-700 space-y-2">
                <p>• <a href="/administration/api-diagnostic" className="text-blue-600 hover:text-blue-800 underline">Diagnostic complet de l'API</a></p>
                <p>• <a href="/administration/test-assign-site" className="text-blue-600 hover:text-blue-800 underline">Test complet d'assignation</a></p>
                <p>• <a href="/administration/user/5" className="text-blue-600 hover:text-blue-800 underline">Page de détail utilisateur</a></p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Container des toasts */}
      <ToastContainer toasts={toasts} position="top-right" />
    </div>
  );
};

export default SimpleAssignTest;
